import { useForm } from 'react-hook-form';
import { Modal, Input, Select, Button } from '@/components/ui';
import { useCreateClient, useUpdateClient } from '@/hooks/useClients';
import type { CreateClientInput, Client } from '@aelita/shared';

export interface ClientFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
}

export function ClientFormModal({ isOpen, onClose, client }: ClientFormModalProps) {
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const isEditing = !!client;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateClientInput>({
    defaultValues: client
      ? {
          name: client.name,
          email: client.email ?? '',
          phone: client.phone ?? '',
          website: client.website ?? '',
          industry: client.industry ?? '',
          status: client.status,
          contractValue: client.contractValue ?? undefined,
          notes: client.notes ?? '',
        }
      : {},
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: CreateClientInput) => {
    try {
      if (isEditing) {
        await updateClient.mutateAsync({
          id: client.id,
          data,
        });
      } else {
        await createClient.mutateAsync(data);
      }
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to save client:', error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Edit Client' : 'Add New Client'}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Company Name"
          {...register('name', { required: 'Name is required' })}
          error={errors.name?.message}
          defaultValue={client?.name}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Email"
            type="email"
            {...register('email')}
            defaultValue={client?.email ?? ''}
          />
          <Input label="Phone" {...register('phone')} defaultValue={client?.phone ?? ''} />
        </div>

        <Input
          label="Website"
          {...register('website')}
          placeholder="https://"
          defaultValue={client?.website ?? ''}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input label="Industry" {...register('industry')} defaultValue={client?.industry ?? ''} />
          <Select
            label="Status"
            {...register('status')}
            options={[
              { value: 'prospect', label: 'Prospect' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'churned', label: 'Churned' },
            ]}
            defaultValue={client?.status ?? 'prospect'}
          />
        </div>

        <Input
          label="Contract Value"
          type="number"
          {...register('contractValue', { valueAsNumber: true })}
          placeholder="0.00"
          defaultValue={client?.contractValue ?? ''}
        />

        <div>
          <label className="label">Notes</label>
          <textarea
            {...register('notes')}
            className="input min-h-[80px]"
            placeholder="Additional notes about this client..."
            defaultValue={client?.notes ?? ''}
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={createClient.isPending || updateClient.isPending}>
            {isEditing ? 'Save Changes' : 'Add Client'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
